import { QueryClient, useQuery } from '@tanstack/react-query'
import { indexedDB } from '../../indexedDB'
import {
  indexedDBQueryKey,
  invalidateIndexedDBRecordCache,
  invalidateIndexedDBTableCache,
} from '../queryKeys'
import type { PendingBulkRequest } from './schema'

const pendingBulkRequestsKey = 'pendingBulkRequests'

type PendingBulkRequestsQueryParams = Pick<PendingBulkRequest, 'operationId'>

type UsePendingBulkRequest<TSelectResponse> = {
  queryParams: PendingBulkRequestsQueryParams
  select?: (data: PendingBulkRequest | null) => TSelectResponse
  enabled?: boolean
}

type UsePendingBulkRequests<TSelectResponse> = {
  select?: (data: PendingBulkRequest[]) => TSelectResponse
  enabled?: boolean
}

export const usePendingBulkRequest = <
  TSelectResponse = PendingBulkRequest | null,
>({
  queryParams,
  select,
  enabled = true,
}: UsePendingBulkRequest<TSelectResponse>) => {
  return useQuery({
    queryKey: indexedDBQueryKey(pendingBulkRequestsKey, queryParams),
    queryFn: async () => {
      return (
        (await indexedDB.pendingBulkRequests.get({
          operationId: queryParams.operationId,
        })) ?? null
      )
    },
    select,
    enabled,
  })
}

export const usePendingBulkRequests = <TSelectResponse = PendingBulkRequest[]>(
  params?: UsePendingBulkRequests<TSelectResponse>
) => {
  return useQuery({
    queryKey: indexedDBQueryKey(pendingBulkRequestsKey),
    queryFn: async () => indexedDB.pendingBulkRequests.toArray(),
    select: params?.select,
    enabled: params?.enabled,
  })
}

export const invalidatePendingBulkRequestRecord = (
  queryClient: QueryClient,
  params: PendingBulkRequestsQueryParams
) => invalidateIndexedDBRecordCache(queryClient, pendingBulkRequestsKey, params)

export const invalidatePendingBulkRequestRecords = (queryClient: QueryClient) =>
  invalidateIndexedDBTableCache(queryClient, pendingBulkRequestsKey)
